<template>
  <div class="pagina-recuperar">
    <div class="tarjeta">
      <img
        :src="require('@/assets/logo.png')"
        alt="Bahiol"
        class="logo"
      >

      <h1 class="titulo">Recuperar contraseña</h1>
      <p class="subtitulo">{{ subtitulo }}</p>

      <!-- Los tres pasos van en la misma pantalla y no en paginas distintas:
           el codigo llega al correo, y en celular eso obliga a salir de la app
           y volver. Cambiar de pagina en medio arriesga perder lo escrito -->
      <div class="pasos">
        <span
          v-for="n in 3"
          :key="n"
          class="punto"
          :class="{ activo: paso === n, hecho: paso > n }"
        />
      </div>

      <!-- PASO 1: el correo -->
      <v-form
        v-if="paso === 1"
        @submit.prevent="pedirCodigo"
      >
        <v-text-field
          v-model="correo"
          class="fg-blue"
          label="Tu correo"
          variant="outlined"
          density="comfortable"
          autocomplete="email"
          autofocus
          prepend-inner-icon="mdi-email-outline"
          :error-messages="errorCorreo"
          @input="intentoPaso = false"
        />

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          :loading="enviando"
        >
          Enviarme el código
        </v-btn>
      </v-form>

      <!-- PASO 2: el codigo -->
      <v-form
        v-else-if="paso === 2"
        @submit.prevent="pasarAClave"
      >
        <v-text-field
          v-model="codigo"
          class="fg-blue"
          label="Código del correo"
          variant="outlined"
          density="comfortable"
          maxlength="6"
          inputmode="numeric"
          placeholder="000000"
          autofocus
          prepend-inner-icon="mdi-shield-key-outline"
          :error-messages="errorCodigo"
          @input="intentoPaso = false"
        />

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
        >
          Continuar
        </v-btn>

        <div class="reenvio">
          <v-btn
            variant="text"
            size="small"
            color="primary"
            :loading="enviando"
            :disabled="esperaReenvio > 0"
            @click="pedirCodigo"
          >
            {{ textoReenvio }}
          </v-btn>
        </div>
      </v-form>

      <!-- PASO 3: la clave nueva -->
      <v-form
        v-else
        @submit.prevent="guardar"
      >
        <v-text-field
          v-model="clave"
          class="fg-blue mb-2"
          label="Nueva contraseña"
          variant="outlined"
          density="comfortable"
          autocomplete="new-password"
          autofocus
          prepend-inner-icon="mdi-lock-outline"
          :type="verClave ? 'text' : 'password'"
          :append-inner-icon="verClave ? 'mdi-eye-off' : 'mdi-eye'"
          :error-messages="errorClave"
          @click:append-inner="verClave = !verClave"
          @input="intentoPaso = false"
        />

        <v-text-field
          v-model="claveConfirmada"
          class="fg-blue"
          label="Repite la contraseña"
          variant="outlined"
          density="comfortable"
          autocomplete="new-password"
          prepend-inner-icon="mdi-lock-check-outline"
          :type="verClave ? 'text' : 'password'"
          :error-messages="errorConfirmar"
          @input="intentoPaso = false"
        />

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          :loading="guardando"
        >
          Cambiar contraseña
        </v-btn>
      </v-form>

      <v-alert
        v-if="aviso"
        type="success"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ aviso }}
      </v-alert>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        {{ error }}
      </v-alert>

      <div class="pie">
        <a
          class="enlace"
          @click="$router.push({ name: 'LoginView' })"
        >Volver a iniciar sesión</a>
      </div>
    </div>
  </div>
</template>

<script>
import apisUsers from '@/apis/apisUsers'
import { REQUERIDO, CORREO, CLAVE } from '@/utils/validaciones'

export default {
  name: 'RecuperarClaveView',
  data() {
    return {
      paso: 1,
      correo: '',
      codigo: '',
      clave: '',
      claveConfirmada: '',
      verClave: false,
      enviando: false,
      guardando: false,
      aviso: '',
      error: '',
      // Los mensajes de error solo aparecen despues de intentar avanzar, para
      // no regañar al usuario mientras todavia esta escribiendo
      intentoPaso: false,
      esperaReenvio: 0,
      temporizador: null
    }
  },
  computed: {
    subtitulo() {
      if (this.paso === 1) return 'Te enviaremos un código a tu correo'
      if (this.paso === 2) return `Escribe el código que enviamos a ${this.correo}`
      return 'Elige tu nueva contraseña'
    },
    errorCorreo() { return this.mensaje(REQUERIDO(this.correo), CORREO(this.correo)) },
    errorCodigo() { return this.mensaje(REQUERIDO(this.codigo)) },
    errorClave() { return this.mensaje(REQUERIDO(this.clave), CLAVE(this.clave)) },
    errorConfirmar() {
      return this.mensaje(
        REQUERIDO(this.claveConfirmada),
        this.claveConfirmada === this.clave || 'Las contraseñas no coinciden'
      )
    },
    textoReenvio() {
      return this.esperaReenvio > 0
        ? `Reenviar en ${this.esperaReenvio}s`
        : 'No me llegó, reenviar'
    }
  },
  beforeUnmount() {
    clearInterval(this.temporizador)
  },
  methods: {
    /** Devuelve el primer fallo, o [] si todo bien o aun no se intento avanzar. */
    mensaje(...resultados) {
      if (!this.intentoPaso) {
        return []
      }
      const fallo = resultados.find(r => r !== true)
      return fallo ? [fallo] : []
    },

    pedirCodigo() {
      this.error = ''
      this.aviso = ''
      this.intentoPaso = true

      if (this.errorCorreo.length) {
        return
      }

      this.enviando = true
      const correo = this.correo.trim().toLowerCase()

      apisUsers.solicitarCodigo(correo, 'recuperacion')
        .then(() => {
          // El aviso es el mismo tenga cuenta ese correo o no. El backend ya
          // responde igual en los dos casos a proposito; si aqui se
          // distinguiera, la pantalla seria un detector de correos registrados
          this.aviso = `Si ese correo tiene una cuenta, le llegará un código. Revisa también la carpeta de spam.`
          this.correo = correo
          this.paso = 2
          this.intentoPaso = false
          this.iniciarEsperaReenvio()
        })
        .catch(err => {
          // Aqui cae el limite de reenvios, que al usuario le sirve tal cual
          this.error = err.message
        })
        .finally(() => {
          this.enviando = false
        })
    },

    /** El codigo no se comprueba contra el servidor hasta el ultimo paso: el
     *  endpoint que lo valida es el mismo que cambia la clave, y consultarlo
     *  antes lo quemaria sin haberla cambiado. */
    pasarAClave() {
      this.error = ''
      this.aviso = ''
      this.intentoPaso = true

      if (this.errorCodigo.length) {
        return
      }

      this.paso = 3
      this.intentoPaso = false
    },

    guardar() {
      this.error = ''
      this.aviso = ''
      this.intentoPaso = true

      if (this.errorClave.length || this.errorConfirmar.length) {
        return
      }

      this.guardando = true

      apisUsers.recuperarContrasena(this.correo, this.codigo.trim(), this.clave)
        .then(() => {
          // Al Login y no directo a la sesion: escribir la clave recien puesta
          // una vez mas ayuda a no volver a olvidarla, y deja claro que quedo
          // cambiada. El correo va en la query para no tener que escribirlo
          this.$router.push({ name: 'LoginView', query: { correo: this.correo } })
        })
        .catch(err => {
          this.error = err.message

          // Si el fallo fue del codigo, lo que hay que corregir esta en el
          // paso 2, no aqui. Se distingue por el texto porque los dos casos
          // responden 400; si algun dia no calza, el error igual queda a la
          // vista y la pantalla sigue siendo usable, solo que sin devolver
          // al usuario al campo que debe arreglar.
          if (/codigo|código/i.test(err.message)) {
            this.paso = 2
            this.codigo = ''
            this.intentoPaso = false
          }
        })
        .finally(() => {
          this.guardando = false
        })
    },

    /** El backend rechaza otro codigo antes de 60s; se refleja en el boton. */
    iniciarEsperaReenvio() {
      clearInterval(this.temporizador)
      this.esperaReenvio = 60

      this.temporizador = setInterval(() => {
        this.esperaReenvio--
        if (this.esperaReenvio <= 0) {
          clearInterval(this.temporizador)
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.pagina-recuperar {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 140px);
  padding: 16px;
  background: linear-gradient(160deg, #dbeafe 0%, #eff6ff 45%, #f8fafc 100%);
}

.tarjeta {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px 24px;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.12);
}

.logo {
  height: 44px;
  display: block;
  margin: 0 auto 14px;
}

.titulo {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111;
  text-align: center;
  margin: 0;
}

.subtitulo {
  font-size: 0.85rem;
  color: #777;
  text-align: center;
  margin: 4px 0 18px;
  min-height: 2.4em;
}

/* Tres puntos y no un stepper de Vuetify: en 420px el stepper se come el
   ancho con etiquetas que no aportan nada */
.pasos {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
}

.punto {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dbeafe;
  transition: background 0.2s, transform 0.2s;
}
.punto.activo {
  background: #1976d2;
  transform: scale(1.35);
}
.punto.hecho {
  background: #90caf9;
}

.reenvio {
  text-align: center;
  margin-top: 8px;
}

.pie {
  margin-top: 18px;
  text-align: center;
  font-size: 0.85rem;
  color: #666;
}

.enlace {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
}
.enlace:hover {
  text-decoration: underline;
}
</style>
